/**
 * The workspace's ONE folder tree ("โฟลเดอร์งาน", docs/adr/0043-unified-folders.md): every page
 * that lists work shows the same tree, in the same order, with the same counts. This module is the
 * shared client state — the tree itself (one cached request per workspace), which folder the
 * person is "in" (kept in the address as `?folder=` and remembered when they change page, so new
 * work is filed where they are), which branches are open, and the small rules behind
 * "จัดเข้าโฟลเดอร์" suggestions.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { api, describeError } from '../api/client.js';
import type { Folder, FolderCounts, FolderScope } from '../api/types.js';
import { FILES_DRAG } from '../lib/filesDrag.js';
import { invalidateSWR, useSWR } from '../lib/swr.js';
import { useSession } from './session.js';

export const MAX_FOLDER_DEPTH = 4;

/** What a folder holds, in the order the folder page lists it. */
export const FOLDER_KINDS = ['design', 'file', 'tor', 'catalog', 'task', 'link'] as const;
export type FolderKind = (typeof FOLDER_KINDS)[number];
export const FOLDER_KIND_LABEL: Record<FolderKind, string> = {
  design: 'งานนำเสนอ/งานออกแบบ',
  file: 'ไฟล์',
  tor: 'TOR',
  catalog: 'สินค้า',
  task: 'งาน/Task',
  link: 'ลิงก์เอกสาร',
};

const EMPTY_COUNTS: FolderCounts = { design: 0, file: 0, tor: 0, catalog: 0, task: 0, link: 0, total: 0, designKinds: {} };

/** "งานนำเสนอ/งานออกแบบ 2 · ไฟล์ 5" — only the kinds that are there. */
export function countsText(c: FolderCounts | null | undefined): string {
  if (!c || !c.total) return 'ว่าง';
  return FOLDER_KINDS.filter((k) => c[k] > 0).map((k) => `${FOLDER_KIND_LABEL[k]} ${c[k]}`).join(' · ');
}

// ---------- the tree (one request per workspace, shared by every page) ----------

const treeKey = (w: string) => `folders:${w}:tree`;

/** After anything that changes folders or what is in them: every open tree re-reads. */
export function invalidateFolders(w?: string | null): void {
  invalidateSWR(w ? `folders:${w}:` : 'folders:');
}

export function useFolderTree(): { folders: Folder[] | null; unfiled: FolderCounts; error: string | null; reload: () => void } {
  const w = useSession().workspace?.id ?? null;
  // Kept on this device too (lib/listStore.ts): the folder bar of หน้าแรก / ไฟล์ is there at once after a reload.
  const swr = useSWR(w ? treeKey(w) : null, () => api.folders(w!), { persist: true });
  const reload = useCallback(() => invalidateFolders(w), [w]);
  const error = swr.error ? describeError(swr.error) : null;
  // A failed first load is an empty tree plus the error, never an endless "loading".
  return { folders: swr.data?.items ?? (error ? [] : null), unfiled: swr.data?.unfiled ?? EMPTY_COUNTS, error, reload };
}

// ---------- pure helpers ----------

export const folderById = (folders: Folder[] | null | undefined, id: string | null | undefined): Folder | null =>
  (id && id !== 'root' ? folders?.find((f) => f.id === id) : null) ?? null;

/** Root → … → folder. Folders on the path that the person may not see are skipped; unknown ids give []. */
export function folderPath(folders: Folder[] | null | undefined, id: string | null | undefined): Folder[] {
  const f = folderById(folders, id);
  if (!f || !folders) return [];
  const byId = new Map(folders.map((x) => [x.id, x]));
  return (f.path?.length ? f.path : [f.id]).map((p) => byId.get(p)).filter((x): x is Folder => !!x);
}

/** "ลูกค้า › กรมป่าไม้ › โครงการ" for "บันทึกไว้ที่: …". */
export const folderTrail = (folders: Folder[] | null | undefined, id: string | null | undefined): string =>
  folderPath(folders, id).map((f) => f.name).join(' › ');

/** Direct children, in the server's order (category nodes first, then by name). */
export const childrenOf = (folders: Folder[] | null | undefined, parentId: string | null): Folder[] => {
  if (!folders) return [];
  if (parentId) return folders.filter((f) => f.parentId === parentId);
  // A folder whose parent is hidden from this person shows at the top level.
  const ids = new Set(folders.map((f) => f.id));
  return folders.filter((f) => !f.parentId || !ids.has(f.parentId));
};

export interface TreeRow {
  folder: Folder;
  /** 0 = top level. */
  level: number;
  hasChildren: boolean;
  expanded: boolean;
}

/** The visible rows of the tree, depth-first. `expanded = null` opens everything (pickers, tests). */
export function treeRows(folders: Folder[] | null | undefined, expanded: ReadonlySet<string> | null): TreeRow[] {
  const out: TreeRow[] = [];
  const walk = (parentId: string | null, level: number) => {
    for (const f of childrenOf(folders, parentId)) {
      const kids = (folders ?? []).some((c) => c.parentId === f.id);
      const open = kids && (expanded === null || expanded.has(f.id));
      out.push({ folder: f, level, hasChildren: kids, expanded: open });
      if (open && level < MAX_FOLDER_DEPTH) walk(f.id, level + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** The folder and everything below it. */
export const subtreeIds = (folders: Folder[] | null | undefined, id: string): string[] => (folders ?? []).filter((f) => f.path?.includes(id) || f.id === id).map((f) => f.id);

/** How many levels there are below this folder (0 = no sub-folder). */
export const levelsBelow = (folders: Folder[] | null | undefined, id: string): number =>
  Math.max(0, ...(folders ?? []).filter((f) => f.path?.includes(id)).map((f) => f.path.length - f.path.indexOf(id) - 1));

/** Folders `moving` (a folder id, or null for an item) may be put into: visible, not itself / below itself, deep enough. */
export function moveTargets(folders: Folder[] | null | undefined, moving: string | null): Folder[] {
  const all = folders ?? [];
  if (!moving) return all;
  const below = levelsBelow(all, moving);
  return all.filter((f) => !f.path.includes(moving) && f.depth + 1 + below <= MAX_FOLDER_DEPTH);
}

// ---------- which branches are open (remembered per workspace on this device) ----------

const expandedKey = (w: string) => `xc.folders.expanded.${w}`;

export function loadExpanded(w: string | null): Set<string> {
  if (!w) return new Set();
  try {
    const v = JSON.parse(localStorage.getItem(expandedKey(w)) ?? '[]') as unknown;
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveExpanded(w: string | null, ids: ReadonlySet<string>): void {
  if (!w) return;
  try {
    localStorage.setItem(expandedKey(w), JSON.stringify([...ids].slice(0, 500)));
  } catch {
    /* private mode: the tree still works, it just forgets */
  }
}

// ---------- the folder the person is in ----------
// Kept per workspace for this tab: pages with the tree write it whenever the selection changes, the
// menu carries it to the next page (`?folder=`), and create / import pages read it to know where
// new work goes ("บันทึกไว้ที่ …").

const currentKey = (w: string) => `xc.folders.current.${w}`;
const listeners = new Set<() => void>();
const memory = new Map<string, string | null>();

export function rememberedFolder(w: string | null | undefined): string | null {
  if (!w) return null;
  if (memory.has(w)) return memory.get(w) ?? null;
  let v: string | null = null;
  try {
    v = sessionStorage.getItem(currentKey(w));
  } catch {
    v = null;
  }
  memory.set(w, v);
  return v;
}

/** `folder`: an id, or null / undefined / 'root' for "not in a particular folder". */
export function rememberFolder(w: string | null | undefined, folder: string | null | undefined): void {
  if (!w) return;
  const v = folder && folder !== 'root' ? folder : null;
  if (rememberedFolder(w) === v) return;
  memory.set(w, v);
  try {
    if (v) sessionStorage.setItem(currentKey(w), v);
    else sessionStorage.removeItem(currentKey(w));
  } catch {
    /* kept in memory for this page load */
  }
  listeners.forEach((fn) => fn());
}

/** The remembered folder of the current workspace (re-renders when it changes). */
export function useRememberedFolder(): string | null {
  const w = useSession().workspace?.id ?? null;
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
    () => rememberedFolder(w),
    () => null,
  );
}

/** Pages that show the tree: a link to one of them keeps the person in the same folder. */
export const FOLDER_PAGES = ['/', '/files', '/presentations', '/tor', '/catalog'] as const;

/** `path` with `?folder=` added when the destination is a folder page and a folder is selected. */
export function withFolder(path: string, folder: string | null | undefined): string {
  if (!folder || folder === 'root') return path;
  const [base = path, qs = ''] = path.split('?');
  if (!(FOLDER_PAGES as readonly string[]).includes(base)) return path;
  const q = new URLSearchParams(qs);
  if (q.has('folder')) return path;
  q.set('folder', folder);
  return `${base}?${q.toString()}`;
}

/**
 * Where a create / import page files its result: the folder named in its address, else the folder
 * the person was last in — as long as it still exists and they may see it. `set` lets them change it.
 */
export function useSaveFolder(fromQuery?: string | null): { folderId: string | null; folder: Folder | null; trail: string; folders: Folder[] | null; set: (id: string | null) => void } {
  const { folders } = useFolderTree();
  const remembered = useRememberedFolder();
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const wanted = chosen !== undefined ? chosen : (fromQuery && fromQuery !== 'root' ? fromQuery : remembered);
  const folder = useMemo(() => folderById(folders, wanted), [folders, wanted]);
  // Until the tree has loaded the wanted id is trusted (the server refuses a folder that is gone or hidden).
  const folderId = folders ? folder?.id ?? null : wanted ?? null;
  return { folderId, folder, trail: folderTrail(folders, folderId), folders, set: setChosen };
}

// ---------- "จัดเข้าโฟลเดอร์" suggestions (plain rules, no AI) ----------

const norm = (s: string) => s.normalize('NFC').toLowerCase().replace(/[\s_\-–—.,()[\]{}/\\|:;'"“”‘’]+/g, ' ').trim();

/**
 * A folder for an item that is in none: (1) the folder tied to the item's customer — the nearest
 * to the top when several are; (2) else the folder whose name appears in the item's title — the
 * longest such name, at least 3 characters. Null when neither rule finds one.
 */
export function suggestFolder(item: { title: string; customerId?: string | null }, folders: Folder[] | null | undefined): Folder | null {
  const all = folders ?? [];
  if (item.customerId) {
    const tied = all.filter((f) => f.customerId === item.customerId).sort((a, b) => a.depth - b.depth);
    if (tied[0]) return tied[0];
  }
  const title = norm(item.title);
  if (!title) return null;
  let best: Folder | null = null;
  let bestLen = 0;
  for (const f of all) {
    const name = norm(f.name);
    if (name.length < 3 || !title.includes(name)) continue;
    if (name.length > bestLen || (name.length === bestLen && best && f.depth > best.depth)) {
      best = f;
      bestLen = name.length;
    }
  }
  return best;
}

// ---------- what is being dragged onto a folder ----------

/** A list item of any kind (`dragItem` in app/organize.ts). */
export const ITEM_DRAG = 'application/x-xcreative-item';
/** A folder of the tree. */
export const FOLDER_DRAG = 'application/x-xcreative-folder';

type WithTransfer = { dataTransfer: DataTransfer | null };
const types = (e: WithTransfer) => (e.dataTransfer ? [...e.dataTransfer.types] : []);

/** Whether this drag can be dropped on a folder (types only — the data is readable on drop). */
export const isFolderDroppable = (e: WithTransfer): boolean => types(e).some((t) => t === ITEM_DRAG || t === FILES_DRAG || t === FOLDER_DRAG);

export type Dropped = { type: 'items'; kind: FolderScope; ids: string[] } | { type: 'folder'; id: string } | null;

export function readDrop(e: WithTransfer): Dropped {
  const dt = e.dataTransfer;
  if (!dt) return null;
  try {
    if (types(e).includes(FOLDER_DRAG)) {
      const id = dt.getData(FOLDER_DRAG);
      return id ? { type: 'folder', id } : null;
    }
    if (types(e).includes(FILES_DRAG)) {
      const v = JSON.parse(dt.getData(FILES_DRAG)) as { ids?: unknown };
      const ids = Array.isArray(v.ids) ? v.ids.filter((x): x is string => typeof x === 'string') : [];
      return ids.length ? { type: 'items', kind: 'file', ids } : null;
    }
    if (types(e).includes(ITEM_DRAG)) {
      const v = JSON.parse(dt.getData(ITEM_DRAG)) as { kind?: FolderScope; id?: string; ids?: string[] };
      const ids = v.ids ?? (v.id ? [v.id] : []);
      return v.kind && ids.length ? { type: 'items', kind: v.kind, ids } : null;
    }
  } catch {
    return null;
  }
  return null;
}

/** Keeps `rememberFolder` in step with a page's selected folder. */
export function useRememberFolder(folder: string | undefined): void {
  const w = useSession().workspace?.id ?? null;
  useEffect(() => {
    rememberFolder(w, folder);
  }, [w, folder]);
}
